/* eslint-disable no-loop-func */
import Alert from 'react-s-alert';
import { observable, action, computed, runInAction } from 'mobx';
import { AppManager, routerStore } from 'core';

import {
  CloneContributionQuery,
  PullContributionQuery,
  ContributionsQuery,
} from 'apps/repository/gql';
import {
  gutConnect,
  gutDisconnect,
  gutIsConnected,
  gutStatus,
  gutReadCommit,
  gutHeadHash,
  gutPull,
  gutGetConnectedPath,
  gutSyncBlobs,
  listenForGutStatus,
  listenForDownload,
  gutResolve,
  gutReset,
  listenForUpload,
  listenForFocus,
  gutResetProject,
} from 'libs/gutClient/electronBridge';

import {
  Status,
  StatusType,
  DownloadsCache,
  DownloadsStatus,
  Commit,
  UploadsStatus,
  UploadsCache,
  StatusMap,
  SyncStateEnum,
} from 'libs/gutClient/types';
import { analytics } from 'core/analytics';
import { statusEqual } from 'libs/gutClient/typeUtils';
import { ContributionApp, ContributionModel } from '../app';
import { ContributionsSubscription } from './gql';
import {
  resolveIdSelection,
  getPathContentsFromTree,
  getTreeNodeFromPath,
  extendTreeNode,
  concatPaths,
  getLastSegment,
  isIllegalPath,
} from './utils';
import { LockingManager } from './lockingManager';
import type { RepoAppManager } from '../appManager';
import { ContributingManager } from './contributingManager';

export type SelectableLocalChange = Status & {
  selected: boolean;
  path: string;
  repoId?: string;
  tabId?: number;
};

export type SelectableLocalChanges = SelectableLocalChange[];

const STATUS_INTERVAL = 10000;

export class SyncRepoAppManager extends AppManager {
  static registryName = 'SyncRepoAppManager';

  @observable repoId: string = '';

  @observable statusMap: StatusMap = new Map();
  @observable headHash: string = null;
  @observable connected = false;
  @observable connectedPath: string = '';
  @observable localCommit: Commit = null;

  @observable remoteHeadCommit: any = null;

  @observable loadingClone = false;
  @observable loadingLocalDrive = false;

  @observable latestContribution: ContributionApp = null;
  @observable numContributions: number = 0;

  @observable remoteTableSelectedRowIds: string[] = [];
  @observable localTableSelectedRowIds: string[] = [];
  @observable selectedLocalChangesTableRowPaths: string[] = [];

  @observable downloadsProgress: DownloadsCache = new Map();
  @observable uploadsProgress: UploadsCache = new Map();

  @observable justCommitedHash: string = null;
  @observable previousHeadHash: string = null;

  @observable statusPending = false;
  @observable statusHasBeenLoaded = false;
  @observable pulling = false;
  @observable syncingBlobs = false;
  @observable pullingOrSyncingBlobs = false;

  @observable skipBannerForHashes: string[] = [];

  @observable queryRemoteTeeLoading = false;

  cloneQuery: CloneContributionQuery = null;
  pullQuery: PullContributionQuery = null;
  remoteTreeQuery: CloneContributionQuery = null;
  contributionsSubscription: ContributionsSubscription = null;

  intervalStatus: any = null;
  lastStatusTimestamp: any = null;

  lockingManager: LockingManager = null;
  contributingManager: ContributingManager = null;

  repoManager: RepoAppManager = null;

  constructor(parent: RepoAppManager, init: any, options: Object) {
    super(parent, init, options);
    this.repoManager = parent;

    if (init?.repoId) {
      this.repoId = init.repoId;
    }

    this.lockingManager = new LockingManager(this);
    this.contributingManager = new ContributingManager(this);
    this.AppRef('latestContribution', ContributionApp);
  }

  @action init = async (repoId: string) => {
    if (this.repoId === repoId) return;
    this.Reset();

    this.repoId = repoId;
    this.QueryLatestContribution();
    this.QueryRemoteTree();
    this.SubscribeToContributions();
    this.checkConnection();
    this.initChannelListeners();

    this.lockingManager.QueryLockedFiles();
    this.contributingManager.init();
  };

  initChannelListeners = () => {
    this.disposers.push(
      listenForGutStatus((/* ...args */) => {
        // console.log('>>> CHANNEL STATUS', ...args);
      }),
    );
    this.disposers.push(
      listenForDownload((event: any, downloadsStatus: DownloadsStatus) => {
        // console.log('>>> CHANNEL DOWNLOAD', downloadsStatus);
        const { downloadsCache, projectKey } = downloadsStatus;
        if (projectKey !== this.repoId) return;
        this.downloadsProgress = downloadsCache;
      }),
    );

    this.disposers.push(
      listenForUpload((event: any, uploadsStatus: UploadsStatus) => {
        // console.log('>>> CHANNEL UPLOAD', uploadsStatus);
        const { uploadsCache, projectKey } = uploadsStatus;
        if (projectKey !== this.repoId) return;
        runInAction(() => {
          this.uploadsProgress = uploadsCache;
          Array.from(uploadsCache.keys())
            .map(fullPath => fullPath.substring(this.connectedPath.length + 1))
            .forEach(path => {
              const contributionItem =
                this.contributingManager.contributionItemsMap[path];
              if (contributionItem)
                contributionItem.hasUploadBeenInitiated = true;
            });
        });
      }),
    );

    this.disposers.push(
      listenForFocus((/* event: any, value: boolean */) => {
        // console.log('>>> CHANNEL FOCUS', value);
        this.LoadStatus();
      }),
    );
  };

  @computed get localHeadRoot() {
    if (!this.localCommit) return null;
    return extendTreeNode(this.localCommit.tree, this.localTableSelectedRowIds);
  }

  @computed get localTree() {
    if (!this.localHeadRoot) return [];
    return this.localHeadRoot.tree;
  }

  @computed get remoteHeadRoot() {
    if (!this.remoteHeadCommit) return null;
    return extendTreeNode(
      this.remoteHeadCommit.tree,
      this.remoteTableSelectedRowIds,
    );
  }

  @computed get remoteTree() {
    if (!this.remoteHeadRoot) return [];
    return this.remoteHeadRoot.tree;
  }

  checkConnection = async () => {
    const [isConnected, err] = await gutIsConnected(this.repoId);
    if (err) {
      this.SetConnected(false);
      console.error(err);
      return;
    }
    this.SetConnected(isConnected);
  };

  @action Reset = () => {
    this.ResetLocalData();
    this.remoteHeadCommit = null;
  };

  @action ResetLocalData = () => {
    this.StopPollingStatus();
    this.headHash = null;
    this.connected = false;
    this.statusMap = new Map();
    this.statusHasBeenLoaded = false;
    this.localCommit = null;

    this.contributingManager.isContributing = false;
  };

  // Not an action
  @action PollStatus = async () => {
    if (this.intervalStatus) return;

    this.intervalStatus = setInterval(() => {
      this.LoadStatus(true);
    }, STATUS_INTERVAL);
    this.disposers.push(() => {
      clearInterval(this.intervalStatus);
    });
  };

  @action StopPollingStatus = async () => {
    clearInterval(this.intervalStatus);
    this.intervalStatus = null;
  };

  @action LoadStatus = async (isSkippable = false) => {
    if (!window.ipc) return;
    if (!this.connected) return;
    if (this.pulling && isSkippable) return;
    if (this.statusPending) return;

    if (
      this.lastStatusTimestamp &&
      Date.now() - this.lastStatusTimestamp < STATUS_INTERVAL &&
      isSkippable
    ) {
      return;
    }

    let statusMap: StatusMap = null;
    let statusErr = null;
    try {
      this.statusPending = true;
      [statusMap, statusErr] = await gutStatus(this.repoId);
      // console.log('statusMap', statusMap);
      if (statusErr) {
        analytics.logError(statusErr);
        return;
      }

      let isThereNewData = true;
      if (statusMap?.size === this.statusMap.size) {
        isThereNewData = false;
        statusMap.forEach((v, k) => {
          if (isThereNewData) return;
          const prevValue = this.statusMap.get(k);
          if (!statusEqual(v, prevValue)) isThereNewData = true;
        });
      }

      if (isThereNewData) {
        runInAction(() => {
          this.statusMap = statusMap;

          this.selectedLocalChangesTableRowPaths =
            this.selectedLocalChangesTableRowPaths.filter(path =>
              this.selectableLocalChangesTableRows.some(
                status => status.path === path,
              ),
            );
          this.statusHasBeenLoaded = true;
        });
      }
    } finally {
      this.lastStatusTimestamp = Date.now();
      this.statusPending = false;
    }
  };

  @action LoadLocalDrive = async () => {
    if (!window.ipc) return;
    this.loadingLocalDrive = true;
    try {
      const [localCommit, err] = await gutReadCommit(this.repoId);
      if (err) {
        analytics.logError(err);
        return;
      }
      this.localCommit = localCommit;
    } finally {
      this.loadingLocalDrive = false;
    }
  };

  @action SubscribeToContributions = () => {
    if (this.contributionsSubscription) return;

    this.contributionsSubscription = new ContributionsSubscription(this, {
      variables: {
        input: {
          id: this.repoId,
        },
      },
    });

    this.contributionsSubscription.process((result: any): any => {
      if (!result) return null;

      this.QueryLatestContribution();
      return null;
    });

    this.contributionsSubscription.StartSubscription();
  };

  connect = async () => {
    const [isConnected, err] = await gutConnect(this.repoId);
    const nothingWasChosen = !isConnected && !err;
    if (nothingWasChosen) return false;
    if (err) {
      throw err;
    }
    this.SetConnected(isConnected, true);
    if (!isConnected) {
      Alert.error('Could not connect to that directory.');
    } else {
      analytics.sendEvent(
        'Project Synced',
        { label: 'project-synced' },
        { ga: false, facebook: false, twitter: false, amp: true },
      );
    }
    return isConnected;
  };

  @computed get connectionStatus() {
    if (this.loadingClone) return 'syncing';
    if (this.connected) return 'synced';
    return 'unconnected';
  }

  @action SetConnected = (isConnected: boolean, skipInitialWork = false) => {
    this.connected = isConnected;
    if (isConnected) {
      if (!skipInitialWork) {
        this.LoadHeadHash();
        this.LoadStatus();
      }
      this.LoadConnectedPath();
      this.PollStatus();
    }
  };

  @action LoadConnectedPath = async () => {
    const [connectedPath, connectedError] = await gutGetConnectedPath(
      this.repoId,
    );
    if (connectedError || !connectedPath) {
      analytics.logError(connectedError);
      return;
    }
    this.connectedPath = connectedPath;
  };

  @action ConnectAndPull = async () => {
    try {
      const connected = await this.connect();
      if (!connected) return;
      await this.PullAndSync();
    } catch (error) {
      analytics.logError(error);
      Alert.error(
        'There has been an error connecting to the project. Please try again later.',
      );
    }
  };

  @action Disconnect = async () => {
    this.StopPollingStatus();
    await gutDisconnect(this.repoId);
    this.ResetLocalData();
  };

  @action PullAndSync = async (fromBanner = false) => {
    try {
      this.pullingOrSyncingBlobs = true;
      const currentLatestHash = this.latestContribution?.version;
      try {
        this.pulling = true;
        const [pulled, pullError] = await gutPull(this.repoId);
        if (pullError) {
          analytics.logError(pullError);
        } else {
          if (fromBanner && currentLatestHash) {
            this.skipBannerForHashes.push(currentLatestHash);
            if (!pulled) Alert.success('Already at latest contribution.');
          }
          if (pulled || !this.headHash) {
            /* await */ this.lockingManager.QueryLockedFiles();
            await this.LoadHeadHash();
            await this.LoadStatus();
          } else {
            this.QueryLatestContribution();
          }
        }
      } finally {
        this.pulling = false;
      }
      await this.SyncBlobs();
      await this.LoadHeadHash();
    } finally {
      this.pullingOrSyncingBlobs = false;
    }
  };

  @action SyncBlobs = async (paths?: string[]) => {
    let didError = false;
    try {
      this.syncingBlobs = true;
      const [syncedBlobs, err] = await gutSyncBlobs(this.repoId, paths);
      if (err) {
        didError = true;
        analytics.logError(err);
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of syncedBlobs.entries()) {
        if (value) {
          didError = true;
          analytics.logError(
            new Error(`Error syncing ${key} reason: ${value.message}`),
          );
        }
      }
    } catch (error) {
      analytics.logError(error);
      didError = true;
    } finally {
      this.syncingBlobs = false;
    }
    if (didError) {
      Alert.error(
        'There was a problem downloading the files. Try again later.',
      );
    }
    await this.LoadStatus(false);
  };

  @computed get remoteCurrentPathContents() {
    if (!this.remoteTree || this.remoteTree.length === 0) return []; // probably should be null to indicate "unloaded" and not "empty repo"
    const basepath = routerStore.route.params.basepath || '';
    const treeContents = getPathContentsFromTree(
      this.remoteTree,
      `/${basepath}`,
    );

    return treeContents;
  }

  @computed get localCurrentPathContents() {
    if (!this.localTree || this.localTree.length === 0) return []; // probably should be null to indicate "unloaded" and not "empty repo"
    const basepath = routerStore.route.params.basepath || '';
    const treeContents = getPathContentsFromTree(
      this.localTree,
      `/${basepath}`,
    );

    return treeContents;
  }

  @computed get localCurrentTreeNode() {
    if (!this.localTree) return null;
    const basepath = routerStore.route.params.basepath || '';
    return getTreeNodeFromPath(this.localHeadRoot, basepath);
  }

  @computed get remoteCurrentTreeNode() {
    if (!this.remoteTree) return null;
    const basepath = routerStore.route.params.basepath || '';
    return getTreeNodeFromPath(this.remoteHeadRoot, basepath);
  }

  @action SelectLocalRow = (ids: string[], selected: boolean) => {
    const addedIds = selected ? ids : [];
    const removedIds = !selected ? ids : [];
    this.localTableSelectedRowIds = resolveIdSelection(
      addedIds,
      removedIds,
      this.localTableSelectedRowIds,
      this.localCurrentTreeNode,
    );
  };

  @action SelectRemoteRow = (ids: string[], selected: boolean) => {
    const addedIds = selected ? ids : [];
    const removedIds = !selected ? ids : [];
    this.remoteTableSelectedRowIds = resolveIdSelection(
      addedIds,
      removedIds,
      this.remoteTableSelectedRowIds,
      this.remoteCurrentTreeNode,
    );
  };

  // eslint-disable-next-line class-methods-use-this
  @computed get currentPath() {
    return `/${routerStore.route.params.basepath || ''}`;
  }

  @computed get breadcrumbs() {
    const segments = this.currentPath
      .split('/')
      .filter(segment => segment !== '');
    segments.unshift('Home');
    return segments;
  }

  @action CloneContribution = () => {
    this.loadingClone = true;

    if (this.cloneQuery) this.cloneQuery.disposer();
    this.cloneQuery = new CloneContributionQuery(this, {
      queryKey: 'CloneContribution',
      // onSuccess: this.cloneContributionSuccess,
      // onError: this.cloneContributionError,
      fetchPolicy: 'no-cache',
    });
    this.cloneQuery.process(
      (result: any) => result,
      this.CloneContributionSuccess,
    );
    this.cloneQuery.query();
  };

  @action CloneContributionSuccess = async (/* result: any */) => {
    // this is now for remote tree queries and checkin mutation responses
    // contribution cloning is done by gutClient when we call gut-pull/fetch
    this.loadingClone = false;
  };

  @action QueryRemoteTree = () => {
    this.queryRemoteTeeLoading = true;
    if (this.remoteTreeQuery) this.remoteTreeQuery.disposer();
    this.remoteTreeQuery = new CloneContributionQuery(this, {
      queryKey: 'queryRemoteTree',
      // onSuccess: this.cloneContributionSuccess,
      // onError: this.cloneContributionError,
    });
    this.remoteTreeQuery.process(
      (result: any) => result,
      this.QueryRemoteTreeSuccess,
    );
    this.remoteTreeQuery.query();
  };

  @action QueryRemoteTreeSuccess = (result: any) => {
    this.queryRemoteTeeLoading = false;
    if (result) {
      this.remoteHeadCommit = result;
      this.remoteTreeQuery.disposer();
      this.remoteTreeQuery.cancel();
    }
  };

  @action QueryLatestContribution = () => {
    const latestContributionQuery = new ContributionsQuery(this, {
      variables: {
        projectId: this.repoId,
        batchSize: 1,
        cursor: null,
        sortBy: 'recent',
      },
      // fetchPolicy: 'network-only',
    });
    latestContributionQuery.process(
      (result: any) =>
        result?.edges.map((edge: any) => ContributionModel.Load(edge.node)),
      this.QueryLatestContributionSuccess,
    );
    latestContributionQuery.process(
      (totalCount: number) => totalCount,
      (totalCount: number) => {
        if (totalCount || totalCount === 0) this.numContributions = totalCount;
      },
      { subPath: 'totalCount' },
    );
    latestContributionQuery.query();
  };

  @action QueryLatestContributionSuccess = (result: any) => {
    if (result) {
      this.latestContribution = result[0] || null;
    }
  };

  @computed get remoteHeadHash() {
    return this.remoteHeadCommit?.hash;
  }

  @action PullContributionSuccess = (result: any) => {
    if (result) {
      // console.log(this.repoId, result);
    }
  };

  @action LoadHeadHash = async () => {
    // eslint-disable-next-line prefer-const
    let [newHeadHash, error] = await gutHeadHash(this.repoId);
    if (error || !newHeadHash) {
      if (error.message.includes('ENOENT')) {
        newHeadHash = null;
      } else {
        analytics.logError(error);
        Alert.error('There was an error getting local head contribution.');
        return;
      }
    }
    if (newHeadHash !== this.headHash) {
      this.headHash = newHeadHash;
      await this.LoadLocalDrive();
    }
  };

  @computed get localStatus() {
    const localStatus: Status[] = [];
    this.statusMap?.forEach((statusItem: Status) => {
      localStatus.push(statusItem);
    });
    return localStatus;
  }

  @computed get localSyncErrors() {
    return this.localStatus.filter(
      status => status.head?.syncStatus.state === SyncStateEnum.SyncFailed,
    );
  }

  @computed get localChanges() {
    const isNewSwapFile = (status: Status) =>
      status.type === StatusType.Added &&
      getLastSegment(status.path).startsWith('~$');

    return this.localStatus.filter(
      status => status.type !== StatusType.NoChange && !isNewSwapFile(status),
    );
  }

  @computed get selectableLocalChangesTableRows() {
    const localChangesAndLocks = [
      ...this.localChanges,
      ...this.lockingManager.localLocksNoChanges,
    ];
    const selectableLocalChangesAndLocks = localChangesAndLocks.map(
      localChange => ({
        ...localChange,
        selected: false,
      }),
    );
    const selectedPathsMap: Record<string, boolean> = {};
    this.selectedLocalChangesTableRowPaths.forEach(selectedPath => {
      selectedPathsMap[selectedPath] = true;
    });
    selectableLocalChangesAndLocks.forEach(selectableLocalChange => {
      if (selectedPathsMap[selectableLocalChange.path]) {
        // eslint-disable-next-line no-param-reassign
        selectableLocalChange.selected = true;
      }
    });
    return selectableLocalChangesAndLocks as SelectableLocalChanges;
  }

  @computed get selectableLocalChangesTableRowsMap() {
    const obj: Record<string, SelectableLocalChange> = {};
    this.selectableLocalChangesTableRows.forEach(item => {
      obj[item.path] = item;
    });
    return obj;
  }

  @computed get contributableLocalChanges() {
    // This is to exclude nochange files which will be locked
    const changeTypes = [
      StatusType.Added,
      StatusType.Conflicted,
      StatusType.Deleted,
      StatusType.Modified,
    ];

    return this.selectableLocalChangesTableRows.filter(
      change =>
        changeTypes.includes(change.type) && !isIllegalPath(change.path),
    );
  }

  @computed get selectedContributableLocalChanges() {
    return this.contributableLocalChanges.filter(change => change.selected);
  }

  @action SelectLocalChange = (path: string, selected: boolean) => {
    const index = this.selectedLocalChangesTableRowPaths.indexOf(path);
    if (index === -1) {
      if (selected) {
        this.selectedLocalChangesTableRowPaths.push(path);
      }
    } else if (!selected) {
      this.selectedLocalChangesTableRowPaths.splice(index, 1);
    }
  };

  @action SelectAllLocalChanges = (
    selectedAll: boolean,
    contributablePathsOnly = false,
  ) => {
    this.selectedLocalChangesTableRowPaths = [];
    if (selectedAll) {
      const allPaths = contributablePathsOnly
        ? this.contributableLocalChanges
        : this.selectableLocalChangesTableRows;
      this.selectedLocalChangesTableRowPaths = allPaths.map(
        change => change.path,
      );
    }
  };

  @computed get numUploads() {
    const items = this.contributingManager.contributionState?.items || [];
    return items.reduce((count, item) => {
      const itemHasBeenInitiated = Boolean(item.hasUploadBeenInitiated);
      const itemWasAlreadyOnCloud = item.fileId && !item.signedUrl;
      return itemHasBeenInitiated || itemWasAlreadyOnCloud ? count + 1 : count;
    }, 0);
  }

  @computed get numUploadsCompleted() {
    // numUploads is the number of initiated uploads and files that normally
    // need to be uploaded but we dont because they are already in the cloud
    // then we substract the files in progress of uploading, because those have
    // been initiatied (so are already counted) but are not completed

    // Alternatively the explicit "has been uploaded" flag is looking at "uploadedHash"
    // but for this case we want the dynamic number that changes as the upload progress
    // processes the files, not the changes in bulk that happen when looking at
    // uploadedHash

    // We prevent this from being negative which can happen when a contribution is
    // interrupted/cancelled but gut main process is still uploading stuff
    return Math.max(this.numUploads - this.uploadsProgress.size, 0);
  }

  @computed get totalExpectedUploads() {
    const items = this.contributingManager.contributionState?.items || [];

    return items.reduce(
      (count, item) =>
        item.type === StatusType.Added || item.type === StatusType.Modified
          ? count + 1
          : count,
      0,
    );
  }

  @computed get areThereNewRemoteCommits() {
    if (this.latestContribution?.version) {
      if (this.skipBannerForHashes.includes(this.latestContribution.version)) {
        return false;
      }
      const didIJustCommitTheRemoteHead =
        this.justCommitedHash === this.latestContribution.version;

      // failsafe against subscription not working
      const isLatestContributionOutdated =
        this.latestContribution.version === this.previousHeadHash;

      return (
        !this.contributingManager.checkingIn &&
        this.headHash !== this.latestContribution.version &&
        !didIJustCommitTheRemoteHead &&
        !isLatestContributionOutdated
      );
    }
    return false;
  }

  openRepoInBrowser = () => {
    window.ipc.invoke('shell-openExternalLink', window.location.href);
  };

  openPath = async (id: string, relativePath: string) => {
    const [connectedPath, connectedError] = await gutGetConnectedPath(
      this.repoId,
    );
    if (connectedError) {
      analytics.logError(connectedError);
      return;
    }
    const absolutePath = concatPaths(connectedPath, relativePath);
    window.ipc.invoke('shell-openPath', absolutePath);
  };

  openCurrentPath = () => {
    this.openPath('', this.currentPath);
  };

  resetChanges = async () => {
    const toReset = this.selectedContributableLocalChanges.map(
      localChange => localChange.path,
    );
    await gutReset(this.repoId, toReset);
    await this.LoadStatus();
  };

  resolveAllConflictKeepCloud = async () => {
    const conflicts = this.localChanges.filter(
      localChange => localChange.type === StatusType.Conflicted,
    );
    // eslint-disable-next-line no-restricted-syntax
    for (const conflict of conflicts) {
      // eslint-disable-next-line no-await-in-loop
      await this.resolveConflict(conflict.path, 'Theirs');
    }
    await this.LoadStatus();
  };

  resolveAllConflictKeepLocal = async () => {
    const conflicts = this.localChanges.filter(
      localChange => localChange.type === StatusType.Conflicted,
    );
    // eslint-disable-next-line no-restricted-syntax
    for (const conflict of conflicts) {
      // eslint-disable-next-line no-await-in-loop
      await this.resolveConflict(conflict.path, 'Ours');
    }
    await this.LoadStatus();
  };

  resolveConflictKeepCloud = async (
    path: string,
    options?: Record<string, any>,
  ) => {
    await this.resolveConflict(path, 'Theirs', options);
    await this.LoadStatus();
  };

  resolveConflictKeepLocal = async (
    path: string,
    options?: Record<string, any>,
  ) => {
    await this.resolveConflict(path, 'Ours', options);
    await this.LoadStatus();
  };

  resolveConflict = async (
    path: string,
    to: string,
    options?: Record<string, any>,
  ) => {
    const [, error] = await gutResolve(this.repoId, to, [path]);
    if (error) {
      analytics.logError(error);
      if (options?.onError) {
        options.onError(error);
      }
    }

    if (!error && options?.onSuccess) {
      options.onSuccess();
    }
  };

  resetProject = async (projectKey?: string) => {
    return gutResetProject(projectKey);
  };
}
